import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Loader2,
  Zap,
  RefreshCw,
  Settings,
  Check,
  Video,
  Download,
  Copy,
  Play,
  Megaphone,
  Share2,
  ExternalLink,
} from "lucide-react";

const PromotionsSection = ({ API_URL, getAuthHeaders, siteName = "AIQueryStudio" }) => {
  const [videoStatus, setVideoStatus] = useState({ video_exists: false, generating: false });
  const [generating, setGenerating] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [generationProgress, setGenerationProgress] = useState({ stage: '', percent: 0 });
  const [paramsLoaded, setParamsLoaded] = useState(false);
  const [savingParams, setSavingParams] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(Date.now());
  const [videoKey, setVideoKey] = useState(0);

  const [videoParams, setVideoParams] = useState({
    prompt: "Create a dynamic, fast-paced reel showcasing the power of AI Query Studio.\n\nHighlight the key benefits:\n- Manage popular databases directly from any browser\n- No installation required\n- Instant access — start working immediately\n- No long-term contracts\n- AI-driven queries that make database work easy for everyone, not just DBAs\n\nAI Query Studio — powerful database management, simplified.\n\nMention main features.\nInclude many screenshots.\nUse fade and slide effects for transitioning.\nStart and finish with the list of database logos.",
    video_type: "short",
    duration: 30,
    voice_over: true,
    voice_type: "excited",
    voice_gender: "female",
    orientation: "portrait",
    video_method: "screenshots"
  });

  useEffect(() => {
    loadVideoParams();
    checkVideoStatus();
  }, []);

  useEffect(() => {
    let refreshInterval;
    if (generating || videoStatus.generating) {
      refreshInterval = setInterval(() => {
        checkVideoStatusSilent();
      }, 5000);
    }
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [generating, videoStatus.generating]);

  const videoRef = useRef(null);

  const loadVideoParams = async () => {
    try {
      const response = await axios.get(`${API_URL}/promo/params`, { headers: getAuthHeaders() });
      setVideoParams(response.data);
      setParamsLoaded(true);
    } catch (error) {
      console.error("Failed to load video params:", error);
      setParamsLoaded(true);
    }
  };

  const saveVideoParams = async (newParams) => {
    setSavingParams(true);
    try {
      await axios.post(`${API_URL}/promo/params`, newParams, { headers: getAuthHeaders() });
      toast.success("Video settings saved");
    } catch (error) {
      console.error("Failed to save video params:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingParams(false);
    }
  };

  const updateParams = (newParams) => {
    setVideoParams(newParams);
  };

  const checkVideoStatusSilent = async () => {
    try {
      const response = await axios.get(`${API_URL}/promo/status`, { headers: getAuthHeaders() });
      if (response.data.generating !== videoStatus.generating) {
        setVideoStatus(prev => ({ ...prev, generating: response.data.generating }));
      }
      if (response.data.generating && !generating) {
        setGenerating(true);
        setGenerationProgress({ stage: 'Generating video...', percent: 50 });
      } else if (!response.data.generating && generating) {
        setGenerating(false);
        setGenerationProgress({ stage: 'Complete!', percent: 100 });
        if (response.data.video_exists) {
          setVideoTimestamp(Date.now());
          setVideoKey(prev => prev + 1);
          setVideoStatus(response.data);
          toast.success("Video generation complete!");
          setTimeout(() => {
            videoRef.current?.load();
            videoRef.current?.play().catch(() => {});
          }, 500);
        }
      }
    } catch (error) {
      console.error("Failed to check video status:", error);
    }
  };

  const checkVideoStatus = async () => {
    if (!paramsLoaded) setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/promo/status`, { headers: getAuthHeaders() });
      setVideoStatus(response.data);
      if (response.data.generating && !generating) {
        setGenerating(true);
        setGenerationProgress({ stage: 'Generating video...', percent: 50 });
      } else if (!response.data.generating && generating) {
        setGenerating(false);
        setGenerationProgress({ stage: 'Complete!', percent: 100 });
        if (response.data.video_exists) {
          toast.success("Video generation complete!");
        }
      }
    } catch (error) {
      console.error("Failed to check video status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const generateVideo = async () => {
    setGenerating(true);
    setGenerationProgress({ stage: 'Initializing...', percent: 5 });
    try {
      const response = await axios.post(`${API_URL}/promo/generate`, videoParams, { headers: getAuthHeaders() });
      if (response.data.status === "success") {
        setGenerationProgress({ stage: 'Complete!', percent: 100 });
        setVideoTimestamp(Date.now());
        setVideoKey(prev => prev + 1);
        toast.success("Video generated successfully!");
        checkVideoStatus();
        setGenerating(false);
      } else if (response.data.status === "generating") {
        setGenerationProgress({ stage: 'Starting video generation...', percent: 10 });
        toast.success(
          <div>
            <strong>Video Generation Started!</strong>
            <p className="text-sm mt-1">{response.data.message || "This takes 2-5 minutes."}</p>
          </div>,
          { duration: 5000 }
        );
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          try {
            pollCount++;
            const statusRes = await axios.get(`${API_URL}/promo/status`, { headers: getAuthHeaders() });
            if (statusRes.data.generating) {
              const estimatedPercent = Math.min(15 + (pollCount * 7), 90);
              const stages = [
                'Capturing app screenshots...',
                'Processing screenshots...',
                'Generating AI voiceover...',
                'Creating video transitions...',
                'Composing final video...',
                'Encoding and finalizing...'
              ];
              const stageIndex = Math.min(Math.floor(pollCount / 2), stages.length - 1);
              setGenerationProgress({ stage: stages[stageIndex], percent: estimatedPercent });
            }
            if (!statusRes.data.generating) {
              clearInterval(pollInterval);
              if (statusRes.data.video_exists) {
                setGenerationProgress({ stage: 'Complete!', percent: 100 });
                setVideoTimestamp(Date.now());
                setVideoKey(prev => prev + 1);
                toast.success(
                  <div>
                    <strong>Video Generation Complete!</strong>
                    <p className="text-sm mt-1">Your new promotional video is ready!</p>
                  </div>,
                  { duration: 8000 }
                );
                setTimeout(() => {
                  videoRef.current?.load();
                  videoRef.current?.play().catch(() => {});
                }, 500);
              }
              checkVideoStatus();
              setGenerating(false);
              setTimeout(() => {
                setGenerationProgress({ stage: '', percent: 0 });
              }, 3000);
            }
          } catch (pollError) {
            console.log("Polling status check failed, will retry...");
          }
        }, 5000);
        setTimeout(() => {
          clearInterval(pollInterval);
          setGenerating(false);
          setGenerationProgress({ stage: '', percent: 0 });
          toast.info("Video generation is taking longer than expected. Please refresh the page to check status.");
        }, 600000);
      } else if (response.data.code === "insufficient_balance") {
        toast.error(
          <div>
            <strong>Insufficient Balance</strong>
            <p className="text-sm mt-1">Your Emergent Universal Key has insufficient balance for video generation.</p>
            <a
              href="https://www.emergentagent.com/settings/universal-key"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm mt-1 text-blue-400 hover:underline block"
            >
              Click here to add balance →
            </a>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(response.data.message || "Video generation failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || error.message;
      if (errorMsg?.includes("insufficient") || errorMsg?.includes("balance")) {
        toast.error(
          <div>
            <strong>Insufficient Balance</strong>
            <p className="text-sm mt-1">Your Emergent Universal Key has insufficient balance.</p>
            <a
              href="https://www.emergentagent.com/settings/universal-key"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm mt-1 text-blue-400 hover:underline block"
            >
              Click here to add balance →
            </a>
          </div>,
          { duration: 10000 }
        );
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('502') || error.message?.includes('timeout')) {
        toast.info(
          <div>
            <strong>Video Generation In Progress</strong>
            <p className="text-sm mt-1">The request timed out, but generation may have started. Click 'Refresh' in a few minutes to check.</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error("Failed to generate video: " + errorMsg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const shortDesc = `${siteName} - Connect to 12 database types from a single powerful interface. Write queries, explore schemas, and manage your data with enterprise-grade security. No long-term commitment required.`;

  return (
    <div className="space-y-6">
      {(videoStatus.generating || generating) && (
        <div className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg animate-pulse">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              <div className="absolute inset-0 w-8 h-8 border-2 border-yellow-500/30 rounded-full animate-ping" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-500 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Video Generation In Progress
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {generationProgress.stage || 'Processing your video... This may take 2-5 minutes.'}
              </p>
              {generationProgress.percent > 0 && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-yellow-500">{generationProgress.stage}</span>
                    <span className="text-muted-foreground">{generationProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
                      style={{ width: `${generationProgress.percent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={checkVideoStatus} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            Promotional Video
            {videoStatus.generating && (
              <Badge variant="outline" className="ml-2 bg-yellow-500/10 text-yellow-500 border-yellow-500/20 animate-pulse">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Generating
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Generate and download a promotional video showcasing {siteName}'s features
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {checkingStatus ? (
            <div className="aspect-video bg-muted/30 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : videoStatus.generating ? (
            <div className="aspect-video bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl flex flex-col items-center justify-center border border-yellow-500/30">
              <Loader2 className="w-16 h-16 text-yellow-500 mb-4 animate-spin" />
              <p className="text-yellow-500 font-medium mb-2">Generating Video...</p>
              <p className="text-xs text-muted-foreground">This usually takes 2-5 minutes</p>
            </div>
          ) : videoStatus.video_exists ? (
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-border">
              <video
                ref={videoRef}
                key={`promo-video-${videoKey}`}
                className="w-full h-full"
                controls
                playsInline
                style={{ backgroundColor: '#000' }}
              >
                <source src={`${API_URL}/promo/video?t=${videoTimestamp}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <div className="aspect-video bg-gradient-to-br from-primary/10 to-muted/30 rounded-xl flex flex-col items-center justify-center border border-dashed border-border">
              <Video className="w-16 h-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No promotional video generated yet</p>
              <p className="text-xs text-muted-foreground">Configure options below and click Generate</p>
            </div>
          )}

          <div className="space-y-4 p-4 bg-muted/20 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Video Generation Settings
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveVideoParams(videoParams)}
                disabled={savingParams}
                className="gap-2"
              >
                {savingParams ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Save Settings
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="video-prompt" className="text-sm">Prompt</Label>
              <Textarea
                id="video-prompt"
                placeholder="Describe your promotional video..."
                value={videoParams.prompt}
                onChange={(e) => updateParams({ ...videoParams, prompt: e.target.value })}
                className="min-h-[80px] text-sm"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Generation Method</Label>
                <Select
                  value={videoParams.video_method}
                  onValueChange={(value) => updateParams({ ...videoParams, video_method: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="screenshots">Screenshots + AI Voice</SelectItem>
                    <SelectItem value="sora">Sora 2 (AI Generated)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {videoParams.video_method === "screenshots"
                    ? "Real app screenshots with AI voiceover"
                    : "Fully AI-generated video"}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Video Type</Label>
                <Select
                  value={videoParams.video_type}
                  onValueChange={(value) => updateParams({ ...videoParams, video_type: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short Form</SelectItem>
                    <SelectItem value="long">Long Form (Pro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Duration</Label>
                <Select
                  value={videoParams.duration.toString()}
                  onValueChange={(value) => updateParams({ ...videoParams, duration: parseInt(value) })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 seconds</SelectItem>
                    <SelectItem value="45">45 seconds</SelectItem>
                    <SelectItem value="60">60 seconds</SelectItem>
                    <SelectItem value="90">90 seconds</SelectItem>
                    <SelectItem value="120">120 seconds</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Orientation</Label>
                <Select
                  value={videoParams.orientation}
                  onValueChange={(value) => updateParams({ ...videoParams, orientation: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sm">Voice Over</Label>
                <Select
                  value={videoParams.voice_over ? "yes" : "no"}
                  onValueChange={(value) => updateParams({ ...videoParams, voice_over: value === "yes" })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {videoParams.voice_over && (
                <div className="space-y-2">
                  <Label className="text-sm">Voice Type</Label>
                  <Select
                    value={videoParams.voice_type}
                    onValueChange={(value) => updateParams({ ...videoParams, voice_type: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excited">Excited</SelectItem>
                      <SelectItem value="very_excited">Very Excited</SelectItem>
                      <SelectItem value="extremely_excited">Extremely Excited</SelectItem>
                      <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="serious">Serious</SelectItem>
                      <SelectItem value="calm">Calm</SelectItem>
                      <SelectItem value="soft">Soft</SelectItem>
                      <SelectItem value="caring">Caring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {videoParams.voice_over && (
                <div className="space-y-2">
                  <Label className="text-sm">Voice Gender</Label>
                  <Select
                    value={videoParams.voice_gender}
                    onValueChange={(value) => updateParams({ ...videoParams, voice_gender: value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {(generating || generationProgress.percent > 0) && (
              <div className="space-y-3 p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/30">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-primary">{generationProgress.stage || 'Starting...'}</span>
                      <span className="font-mono text-primary">{generationProgress.percent}%</span>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-green-400 transition-all duration-500 ease-out"
                    style={{ width: `${generationProgress.percent}%` }}
                  />
                </div>
                {generationProgress.percent < 100 ? (
                  <p className="text-xs text-muted-foreground">
                    Generating your promotional video... Please wait, this may take 2-3 minutes.
                  </p>
                ) : (
                  <p className="text-xs text-green-500 font-medium">
                    Video generation complete! Your new video is ready below.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center gap-4 pt-2">
              <Button
                onClick={generateVideo}
                disabled={generating || videoStatus.generating}
                className="gap-2"
              >
                {(generating || videoStatus.generating) ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    {videoStatus.video_exists ? "Regenerate Video" : "Generate Video"}
                  </>
                )}
              </Button>
              {(generating || videoStatus.generating) && (
                <p className="text-xs text-muted-foreground">
                  Video generation takes 2-5 minutes. You'll be notified when complete.
                </p>
              )}
            </div>
          </div>

          <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Zap className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-sm mb-1">AI-Powered Video Generation with Sora 2</h4>
                <p className="text-xs text-muted-foreground mb-2">
                  Generate professional promotional videos using OpenAI's Sora 2 model.
                  Videos are created based on your product features and branding.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://openai.com/sora"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded hover:bg-purple-500/30 transition-colors inline-flex items-center gap-1"
                  >
                    OpenAI Sora 2
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://www.emergentagent.com/settings/universal-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded hover:bg-blue-500/30 transition-colors inline-flex items-center gap-1"
                  >
                    Add Balance (Universal Key)
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-primary">12s</p>
              <p className="text-sm text-muted-foreground">Duration</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-primary">720p</p>
              <p className="text-sm text-muted-foreground">Quality</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-primary">MP4</p>
              <p className="text-sm text-muted-foreground">Format</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              className="flex-1 gap-2"
              disabled={!videoStatus.video_exists}
              onClick={() => {
                const link = document.createElement('a');
                link.href = `${API_URL}/promo/video`;
                link.download = `${siteName}-Promo-Video.mp4`;
                link.click();
                toast.success('Video download started!');
              }}
            >
              <Download className="w-4 h-4" />
              Download Video
            </Button>
            <Button
              variant="outline"
              className="flex-1 gap-2"
              disabled={!videoStatus.video_exists}
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/api/promo/video`);
                toast.success('Video link copied to clipboard!');
              }}
            >
              <Copy className="w-4 h-4" />
              Copy Link
            </Button>
            {videoStatus.video_exists && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={generateVideo}
                disabled={generating || videoStatus.generating}
              >
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-500" />
            Marketing Assets
          </CardTitle>
          <CardDescription>
            Ready-to-use promotional materials for your marketing campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-border rounded-lg space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Video Highlights
              </h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• 12 Database types supported</li>
                <li>• Powerful query editor with syntax highlighting</li>
                <li>• Real-time metrics dashboard</li>
                <li>• Enterprise-grade security</li>
                <li>• One-click CSV/JSON export</li>
                <li>• No long-term commitment</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Share2 className="w-4 h-4 text-blue-500" />
                Suggested Channels
              </h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Social media (LinkedIn, Twitter, YouTube)</li>
                <li>• Email marketing campaigns</li>
                <li>• Website landing page</li>
                <li>• Product Hunt launch</li>
                <li>• Developer community forums</li>
                <li>• Paid advertising (Google, Facebook)</li>
              </ul>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm text-muted-foreground mb-3">Share on social media:</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=Check out ${siteName} - The ultimate database management tool! 12 database types, powerful query editor, real-time metrics. No long-term commitment!&url=${encodeURIComponent(window.location.origin)}`, '_blank')}
              >
                Twitter/X
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank')}
              >
                LinkedIn
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`, '_blank')}
              >
                Facebook
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Key Marketing Messages</CardTitle>
          <CardDescription>Copy-ready text for your promotional materials</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium">Tagline</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText('Manage Your Databases Like a Pro');
                  toast.success('Copied!');
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-muted-foreground">Manage Your Databases Like a Pro</p>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium">Short Description</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(shortDesc);
                  toast.success('Copied!');
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{shortDesc}</p>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium">Feature List</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText('✓ 12 Database Types (SQL Server, MySQL, PostgreSQL, MongoDB, Oracle, SQLite, MariaDB, Redis, Cassandra, DynamoDB, Couchbase, Firebase)\n✓ Monaco Editor with Syntax Highlighting\n✓ Real-Time Metrics Dashboard\n✓ Query History & Saved Queries\n✓ One-Click CSV/JSON Export\n✓ Visual Database Explorer\n✓ Enterprise Security\n✓ No Long-Term Commitment');
                  toast.success('Copied!');
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <ul className="text-muted-foreground text-sm space-y-1">
              <li>✓ 12 Database Types (SQL Server, MySQL, PostgreSQL, MongoDB, Oracle, SQLite, MariaDB, Redis, Cassandra, DynamoDB, Couchbase, Firebase)</li>
              <li>✓ Monaco Editor with Syntax Highlighting</li>
              <li>✓ Real-Time Metrics Dashboard</li>
              <li>✓ Query History & Saved Queries</li>
              <li>✓ One-Click CSV/JSON Export</li>
              <li>✓ Visual Database Explorer</li>
              <li>✓ Enterprise Security</li>
              <li>✓ No Long-Term Commitment</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromotionsSection;
